import React, { forwardRef, useState } from 'react'
import { css } from '@emotion/react'
import { colors, spacing, borderRadius } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { type DomainMessages, useDomainMessage } from 'domains/collections/utils/domain/intl'
import { type ErrorRecordType } from 'utils/react-hook-form/reactHookForm.types'
import { CollectionsFormInput } from './CollectionsFormInput'

const HEX_COLOR_LENGTH = 7
const HEX_COLOR_REGEX = '#[a-fA-F0-9]{6}'

export interface Props {
  identifier: string
  errorMessage?: DomainMessages
  placeholderMessageId?: DomainMessages
  errors?: ErrorRecordType
  labelId?: DomainMessages
  defaultValue?: string
}

const colorInputStyles = css({
  opacity: 0,
  width: '100%',
  height: '100%',
  cursor: 'pointer',
})

const Wrapper = styled.div<{ color: string }>(
  {
    position: 'absolute',
    right: spacing.X16,
    top: spacing.X40,
    width: 24,
    height: 24,
    border: `1px solid ${colors.GRAYSCALE.X20}`,
    borderRadius: borderRadius.X4,
    cursor: 'pointer',
  },
  ({ color }) => ({ backgroundColor: color })
)

const wrapperStyles = css({
  position: 'relative',
})

export const CollectionsFormColorInput = forwardRef<HTMLInputElement, Props>(
  (
    { errors, errorMessage, labelId, identifier, defaultValue, placeholderMessageId, ...rest },
    ref
  ) => {
    const [text, setText] = useState(defaultValue || '')
    const [color, setColor] = useState(defaultValue)
    const invalidValueMessage = useDomainMessage(errorMessage)

    const onTextChange = event => {
      const { value } = event.target
      setText(value)

      if (RegExp(HEX_COLOR_REGEX).test(value)) {
        setColor(value)
      }
    }

    const onColorChange = event => {
      setColor(event.target.value)
      setText(event.target.value)
    }

    const pattern = text ? HEX_COLOR_REGEX : undefined

    return (
      <div css={wrapperStyles}>
        <CollectionsFormInput
          {...rest}
          labelId={labelId}
          placeholderMessageId={placeholderMessageId}
          identifier={identifier}
          ref={ref}
          value={text}
          maxLength={HEX_COLOR_LENGTH}
          onChange={onTextChange}
          pattern={pattern}
          title={invalidValueMessage}
          errors={errors}
          errorMessage={errorMessage}
        />

        <Wrapper color={color}>
          <input type="color" value={color} onChange={onColorChange} css={colorInputStyles} />
        </Wrapper>
      </div>
    )
  }
)
